import {
  getAverageAllocationInPeriod,
  MonthYearPeriod,
  roundProjectAllocationPercents,
  roundToNearest,
  USER_ROUNDING_NUM_DECIMALS,
} from '@common/neo.tax';

import { Contributor, AscProjectPerContributor } from '@neo.tax/asc/hooks/useContributor';
import {
  AscAllocationProjectDetails,
  AscContributorProjectAllocation,
  ContributorAllocationsInPeriod,
  ContributorProjectAllocation,
  ContributorProjectPercents,
} from '@neo.tax/asc/types';

const getProjectAllocationsInPeriod = (
  contributor: Contributor,
  ascProjects: AscAllocationProjectDetails[],
  period: MonthYearPeriod,
  selectedProjectId?: string,
): ContributorProjectPercents => {
  const ascProjectIdToAscProjectPerContributorInPeriod = getAscProjectIdToAscProjectPerContributorInPeriod(
    contributor,
    period,
  );

  const selectedIdToProjectAllocations = new Map<string, ContributorProjectAllocation>();
  const projectIdToProjectAllocations = new Map<string, ContributorProjectAllocation>();
  for (const ascProject of ascProjects) {
    const projectPerContributorInPeriod = ascProjectIdToAscProjectPerContributorInPeriod.get(ascProject.id);
    const projectAllocationPercent = projectPerContributorInPeriod?.averagePercent ?? 0;
    const ascProjectMonthsPerContributor =
      projectPerContributorInPeriod?.ascProjectPerContributor.ascProjectMonthsPerContributor ?? [];

    const projectAllocation: ContributorProjectAllocation = {
      projectId: ascProject.id,
      projectName: ascProject.name,
      allocationPercent: projectAllocationPercent,
      originalAllocationPercent: projectAllocationPercent,
      lastAllocationPercentOnProjectPercentUpdate: projectAllocationPercent,
      projectMonthsPerContributor: ascProjectMonthsPerContributor,
      capitalizationStatus: ascProject.capitalizationStatus,
      capitalizationStartDate: ascProject.capitalizationStartDate ? new Date(ascProject.capitalizationStartDate) : null,
      capitalizationEndDate: ascProject.capitalizationEndDate ? new Date(ascProject.capitalizationEndDate) : null,
    };
    // Always include the selected project in the assigned projects
    if (ascProject.id === selectedProjectId) {
      selectedIdToProjectAllocations.set(ascProject.id, projectAllocation);
    } else {
      projectIdToProjectAllocations.set(ascProject.id, projectAllocation);
    }
  }

  return {
    projectIdToProjectAllocations,
    selectedIdToProjectAllocations,
  };
};

export const initializeAllocationsInPeriod = ({
  ascContributorProjectAllocations,
  ascProjects,
  contributor,
  period,
}: {
  ascContributorProjectAllocations: AscContributorProjectAllocation[];
  contributor: Contributor;
  period: MonthYearPeriod;
  ascProjects: AscAllocationProjectDetails[];
}): ContributorAllocationsInPeriod => {
  const { projectIdToProjectAllocations } = getProjectAllocationsInPeriod(contributor, ascProjects, period);

  const mergedProjectAllocations = new Map<string, ContributorProjectAllocation>();

  // Round the per-project percents as a group so they never sum past 100% and non-project time can
  // never go negative; the residual is the non-project percent.
  const { nonProjectPercent, projectPercents } = roundProjectAllocationPercents(
    ascContributorProjectAllocations.map((allocation) => allocation.allocationPercentAvg),
  );

  ascContributorProjectAllocations.forEach((viewAllocation, index) => {
    const allocationPercent = projectPercents[index];

    // Get month-level data from contributor if it exists
    const monthLevelData = projectIdToProjectAllocations.get(viewAllocation.ascProjectId);

    const contributorProjectAllocation: ContributorProjectAllocation = {
      projectId: viewAllocation.ascProjectId,
      projectName: viewAllocation.ascProjectName.trim(),
      allocationPercent,
      originalAllocationPercent: allocationPercent,
      lastAllocationPercentOnProjectPercentUpdate: allocationPercent,
      projectMonthsPerContributor: monthLevelData?.projectMonthsPerContributor || [],
      capitalizationStatus: viewAllocation.capitalizationStatus,
      capitalizationStartDate: viewAllocation.capitalizationStartDate
        ? new Date(viewAllocation.capitalizationStartDate)
        : null,
      capitalizationEndDate: viewAllocation.capitalizationEndDate
        ? new Date(viewAllocation.capitalizationEndDate)
        : null,
    };

    mergedProjectAllocations.set(viewAllocation.ascProjectId, contributorProjectAllocation);
  });

  return {
    nonProjectPercent: {
      allocationPercent: nonProjectPercent,
      originalAllocationPercent: nonProjectPercent,
      lastAllocationPercentOnProjectPercentUpdate: nonProjectPercent,
    },
    projectPercents: {
      projectIdToProjectAllocations: mergedProjectAllocations,
      selectedIdToProjectAllocations: new Map(),
    },
  };
};

type AscProjectPerContributorInPeriod = {
  ascProjectPerContributor: AscProjectPerContributor;
  averagePercent: number;
  period: MonthYearPeriod;
  ascProjectName: string;
};

export type AscProjectIdToAscProjectPerContributorInPeriod = Map<string, AscProjectPerContributorInPeriod>;

export const getAscProjectIdToAscProjectPerContributorInPeriod = (
  contributor: Contributor,
  period: MonthYearPeriod,
): AscProjectIdToAscProjectPerContributorInPeriod => {
  const allocationFloatPerAscProjectPerContributorInPeriod = new Map<AscProjectPerContributorInPeriod, number>();
  const ascProjectsPerContributorInPeriod = contributor.ascProjectsPerContributor.map(
    (ascProjectPerContributor): AscProjectPerContributorInPeriod => {
      // they are filtered by the period selector in the query. This assumes that you've re-fetched the query with the new period
      const ascProjectMonthsPerContributorInPeriod = ascProjectPerContributor.ascProjectMonthsPerContributor;

      const _allocationPercent = getAverageAllocationInPeriod(ascProjectMonthsPerContributorInPeriod, period);
      const allocationPercent = roundToNearest(_allocationPercent, USER_ROUNDING_NUM_DECIMALS);

      const ascProjectPerContributorInPeriod = {
        ascProjectPerContributor: {
          ...ascProjectPerContributor,
          projectMonthsPerContributor: ascProjectMonthsPerContributorInPeriod,
        },
        averagePercent: allocationPercent,
        period,
        ascProjectName: ascProjectPerContributor.ascProject.name,
      };
      allocationFloatPerAscProjectPerContributorInPeriod.set(ascProjectPerContributorInPeriod, allocationPercent);

      return ascProjectPerContributorInPeriod;
    },
  );

  return ascProjectsPerContributorInPeriod.reduce(
    (
      acc: AscProjectIdToAscProjectPerContributorInPeriod,
      projectPerContributorInPeriod: AscProjectPerContributorInPeriod,
    ) => {
      const ascProjectId = projectPerContributorInPeriod.ascProjectPerContributor.ascProject.id;
      acc.set(ascProjectId, projectPerContributorInPeriod);
      return acc;
    },
    new Map<string, AscProjectPerContributorInPeriod>(),
  );
};
